import { Dispatch, RefObject, SetStateAction } from 'react';

import { FormikValues } from 'formik';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { NextRouter, useRouter } from 'next/router';

import { EventType } from '~/components/Event';
import UserLoginForm from '~/components/User/UserLoginForm';
import { firebase, useFirebaseApp } from '~/lib/firebase';
import { MICROSITE_UID } from '~/lib/utils/env';

const { publicRuntimeConfig } = getConfig();

const DefaultRegisterFrom = dynamic(() => import('~/components/User/RegisterForms/Default'));

const ExtChInselSpitalBernGynRegisterForm = dynamic(
    () => import('~/components/User/RegisterForms/ExtChInselSpitalBernGyn')
);

const getEmailFromSessionStorage = (): string => {
    if (typeof window !== 'undefined' && sessionStorage) {
        return sessionStorage.getItem('auth/email-already-exists') || '';
    }
    return '';
};

const removeEmailAlreadyExists = (): void => {
    if (typeof window !== 'undefined' && sessionStorage) {
        sessionStorage.removeItem('auth/email-already-exists');
    }
};

export interface IOnRegistrationSubmitProps {
    values: FormikValues;
    setSubmitting: (isSubmitting: boolean) => void;
    setSubmitError: Dispatch<SetStateAction<string | null>>;
}

export interface IHandleUserRegistrationProps extends IOnRegistrationSubmitProps {
    app: firebase.app.App;
    router: NextRouter;
    event: EventType | undefined;
    onLogin?: () => void;
    onRegistrationSuccess?: () => void;
}

const handleUserRegistration = async ({
    app,
    router,
    event,
    values,
    setSubmitting,
    setSubmitError,
    onRegistrationSuccess,
    onLogin
}: IHandleUserRegistrationProps) => {
    try {
        setSubmitting(true);
        setSubmitError('');

        const password = values.password;
        const user_data = { ...values };
        delete user_data.password;

        const response = await fetch(`${publicRuntimeConfig.basePath}/api/account/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                user_data,
                password,
                locale: router.locale,
                event_id: event?.id || ''
            })
        });

        const response_data = await response.json();

        if (response_data.token) {
            await app.auth().signInWithCustomToken(response_data.token);

            if (onRegistrationSuccess) {
                onRegistrationSuccess();
            } else {
                location.reload();
            }
            return;
        } else {
            setSubmitError(
                response_data.error && response_data.error.message
                    ? response_data.error.message
                    : 'Internal Server Error'
            );
            if (response_data.error && response_data.error.code === 'auth/email-already-exists') {
                if (onLogin) {
                    sessionStorage.setItem('auth/email-already-exists', values.email);
                    UserLoginForm.removeEmailUserNotFound();
                    onLogin();
                }
            }
        }
    } catch (e) {
        const err = e as Error;
        setSubmitError(err.message);
    }
};

interface IUserRegisterFormComposition {
    getEmailAlreadyExists: () => string;
    removeEmailAlreadyExists: () => void;
}

const UserRegisterForm: React.FunctionComponent<{
    event: EventType | undefined;
    initialFocusRef?: RefObject<HTMLInputElement> | undefined;
    onLogin?: () => void;
    onRegistrationSuccess?: () => void;
    initialFormValues?: object;
}> &
    IUserRegisterFormComposition = ({
    event,
    initialFocusRef,
    onLogin,
    onRegistrationSuccess,
    initialFormValues = {}
}) => {
    const router = useRouter();
    const { app } = useFirebaseApp();

    const RegisterForm =
        MICROSITE_UID === 'c3883291-cfe2-48df-9c94-583626c3305f' // Insel Spital Bern Gyn
            ? ExtChInselSpitalBernGynRegisterForm
            : DefaultRegisterFrom;

    return (
        <RegisterForm
            initialFocusRef={initialFocusRef}
            onRegistrationSubmit={(props) => {
                return handleUserRegistration({
                    app,
                    router,
                    event,
                    onRegistrationSuccess,
                    onLogin,
                    ...props
                });
            }}
            onLogin={onLogin}
            initialFormValues={initialFormValues}
        />
    );
};

UserRegisterForm.getEmailAlreadyExists = getEmailFromSessionStorage;
UserRegisterForm.removeEmailAlreadyExists = removeEmailAlreadyExists;

export default UserRegisterForm;
